import { useState, useEffect, useRef, Fragment } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sparkles,
  BookmarkPlus,
  Mic,
  BookOpen,
  Layers,
  Copy,
  Edit3,
  Trash2,
  Save,
  X,
  Check,
  Crown,
  RotateCcw,
} from 'lucide-react'
import DemoCursor from './DemoCursor'

// Prayer demo — looped scripted walkthrough of both Prayer tabs and the
// interactions on each. Visual fidelity mirrors the real Prayer surface
// (src/pages/PrayerGenerator.jsx + src/components/prayer/*).
//
// Scenes (cumulative state — each beat adds to the picture):
//   A — Generate: pick a starter chip → text expands → Shape with Scripture
//   B — Journal: switch tabs, open a prayer, Mark Answered (sparkle)
//   C — Edit:    open another prayer, edit text inline, save
//   D — Delete:  open another, delete, fade out
//   E — Topics:  toggle to By Topic, show topic cards
//   then reset and loop.

// ─── Starter chips (mirror CategoryStarterChips.STARTERS) ─────────────
const STARTERS = [
  { id: 'gratitude', label: 'Gratitude', emoji: '🙏', starter: 'Father, thank you for ' },
  { id: 'healing', label: 'Healing', emoji: '💚', starter: "Father, I'm asking for healing for " },
  { id: 'guidance', label: 'Guidance', emoji: '🧭', starter: 'Father, I need wisdom about ' },
  { id: 'comfort', label: 'Comfort', emoji: '🕊️', starter: "Father, I'm sitting with grief over " },
  {
    id: 'strength',
    label: 'Strength',
    emoji: '💪',
    starter: "Father, I'm weary and need your strength for ",
  },
  { id: 'confession', label: 'Confession', emoji: '🪞', starter: 'Father, I confess ' },
  {
    id: 'intercession',
    label: 'Intercession',
    emoji: '🤲',
    starter: 'Father, please move in the life of ',
  },
  { id: 'wisdom', label: 'Wisdom', emoji: '📖', starter: 'Father, I need your wisdom on ' },
]

// User extension after the Gratitude starter — what the demo "types".
const GRATITUDE_EXTENSION =
  'the strength to get through this week, the family around me, and the way You keep showing up even when I forget to look.'

// Topic palette — matches the spirit of the real topicColor.js.
const TOPIC_COLORS = {
  family: '#d4748a',
  college: '#e9c349',
  daily: '#7ec096',
}

// Preset prayers — populate the journal so the demo lands in a lived-in
// state, not an empty one. IDs are stable so timeline events can target.
const INITIAL_PRAYERS = [
  {
    id: 1,
    content:
      "Father, I'm asking for healing for my mother — the diagnosis came in last week and I don't know how to carry this. Give her peace. Give us all peace, and the doctors steady hands.",
    topicId: 'family',
    createdAt: 'Apr 22, 7:33 PM',
    source: 'shaped',
    answered: false,
  },
  {
    id: 2,
    content:
      "Father, I come before You with a heart full of uncertainty about my path in college. I need Your wisdom to guide me toward the calling You've placed in me, not the one others expect of me.",
    topicId: 'college',
    createdAt: 'Apr 21, 8:22 PM',
    source: 'shaped',
    answered: false,
  },
  {
    id: 3,
    content:
      'Father, thank You for peace — the peace that transcends all understanding, the peace that guards my heart and mind in Christ Jesus. I felt it today, in the middle of a hard week, and I want to remember.',
    topicId: 'daily',
    createdAt: 'Apr 21, 8:18 PM',
    source: 'typed',
    answered: false,
  },
]

const TOPIC_LABEL = {
  family: 'Family',
  college: 'College',
  daily: 'Daily',
}

// Edits applied during Scene C — small, character-typed additions so the
// edit animation has something to play with.
const EDIT_TARGET_ID = 2
const EDIT_ADDITION = ' Help me trust the unfolding, not just the destination.'

// AI-shaped prayer streamed into the Shape modal. Two short paragraphs
// with two scripture references so visitors see what "Shape with
// Scripture" actually produces — verses woven into the user's own words.
const SHAPED_PRAYER =
  "Father, I lift my heart to You in gratitude. Thank You for the strength to step through every hour of this week — Your mercies new every morning, as Lamentations 3:22-23 promises. Thank You for the family You've placed around me; I do not deserve them and yet there they are.\n\nAnd thank You for showing up in the small moments, even when my eyes are too tired or my mind too scattered to look for You. May Your peace, which surpasses all understanding, guard my heart and mind in Christ — Philippians 4:7. In Jesus' name, amen."

// ─── Sub-components ───────────────────────────────────────────────────

function OrnamentDivider({ className = '' }) {
  return (
    <svg viewBox="0 0 120 10" className={className} aria-hidden="true">
      <line x1="0" y1="5" x2="45" y2="5" stroke="currentColor" strokeWidth="0.5" opacity="0.35" />
      <line x1="75" y1="5" x2="120" y2="5" stroke="currentColor" strokeWidth="0.5" opacity="0.35" />
      <path d="M 50 5 L 52 3 L 54 5 L 52 7 Z" fill="currentColor" opacity="0.55" />
      <path d="M 60 5 L 64 1 L 68 5 L 64 9 Z" fill="currentColor" />
      <path d="M 74 5 L 76 3 L 78 5 L 76 7 Z" fill="currentColor" opacity="0.55" />
    </svg>
  )
}

function AfternoonGlyph() {
  // Same tiny sun used in TimeOfDayGreeting / ornaments.jsx — kept inline
  // to avoid pulling the real prayer barrel into a demo file.
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-3.5 h-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      style={{ color: '#e9c349', opacity: 0.8 }}
    >
      <circle cx="12" cy="12" r="4" strokeWidth="1.5" />
      <line x1="12" y1="3" x2="12" y2="5" strokeWidth="1" strokeLinecap="round" />
      <line x1="12" y1="19" x2="12" y2="21" strokeWidth="1" strokeLinecap="round" />
      <line x1="3" y1="12" x2="5" y2="12" strokeWidth="1" strokeLinecap="round" />
      <line x1="19" y1="12" x2="21" y2="12" strokeWidth="1" strokeLinecap="round" />
      <line x1="5.8" y1="5.8" x2="7.2" y2="7.2" strokeWidth="1" strokeLinecap="round" />
      <line x1="16.8" y1="7.2" x2="18.2" y2="5.8" strokeWidth="1" strokeLinecap="round" />
      <line x1="5.8" y1="18.2" x2="7.2" y2="16.8" strokeWidth="1" strokeLinecap="round" />
      <line x1="16.8" y1="16.8" x2="18.2" y2="18.2" strokeWidth="1" strokeLinecap="round" />
    </svg>
  )
}

function PrayerHeader({ tab }) {
  return (
    <div className="relative mb-6">
      {/* Candle whisper — same asset the real Prayer page uses */}
      <img
        src="/prayer-journal/candle-bg.webp"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 w-[280px] max-w-[45%] opacity-[0.18] mix-blend-screen select-none"
      />
      <div className="relative">
        <h1
          className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight"
          style={{ color: '#dce1ff' }}
        >
          Prayer
        </h1>
        <p className="mt-1 text-xs uppercase tracking-[0.18em]" style={{ color: '#6a7494' }}>
          private to you
        </p>
        <div className="mt-3 flex items-center gap-1.5 text-xs italic" style={{ color: '#8891b0' }}>
          <AfternoonGlyph />
          <span>Midday prayer — a pause in the running</span>
        </div>
        {tab === 'generate' && (
          <p
            className="mt-3 font-heading text-sm italic leading-relaxed"
            style={{ color: '#8891b0' }}
          >
            &ldquo;Who in your life is far from God? Lift them up.&rdquo;
          </p>
        )}
      </div>
    </div>
  )
}

function Tabs({ tab }) {
  return (
    <div className="flex gap-1 mb-7" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      {['generate', 'journal'].map(t => {
        const active = tab === t
        return (
          <div
            key={t}
            className="relative px-5 py-3 text-sm font-medium tracking-wide"
            style={{ color: active ? '#e9c349' : '#8891b0' }}
          >
            {t === 'generate' ? 'Generate' : 'Journal'}
            {active && (
              <motion.span
                layoutId="prayer-tab-underline"
                className="absolute bottom-0 left-4 right-4"
                style={{ height: 2, background: '#e9c349', borderRadius: 999 }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function StarterChips({ activeStarterId }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-[0.15em]" style={{ color: '#6a7494' }}>
          Start with
        </span>
        <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
      </div>
      <p className="text-xs mb-2" style={{ color: '#8891b0' }}>
        Pick a starter — we'll place your cursor where you need it
      </p>
      <div className="flex flex-wrap gap-2">
        {STARTERS.map(s => {
          const active = activeStarterId === s.id
          return (
            <div
              key={s.id}
              data-cursor-target={`starter-${s.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
              style={{
                background: active ? 'rgba(233,195,73,0.18)' : 'rgba(34,41,65,0.5)',
                color: active ? '#e9c349' : '#8891b0',
                border: `1px solid ${active ? 'rgba(233,195,73,0.4)' : 'transparent'}`,
                transition: 'all 200ms ease',
              }}
            >
              <span className="text-xs">{s.emoji}</span>
              {s.label}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TextareaCard({ text, isTyping }) {
  return (
    <div
      className="relative rounded-2xl p-5"
      style={{
        background: 'rgba(20,26,50,0.55)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <label className="block text-sm font-medium mb-2" style={{ color: '#dce1ff' }}>
        What's on your heart?
      </label>
      <div className="relative">
        <div
          className="w-full min-h-[160px] rounded-lg p-4 pr-12 text-base italic leading-relaxed"
          style={{
            background: '#0b1229',
            border: '1px solid rgba(255,255,255,0.08)',
            color: text ? '#dce1ff' : '#5a6483',
            fontFamily: "'Noto Serif', Georgia, serif",
          }}
        >
          {text ? (
            <>
              {text}
              {isTyping && (
                <span
                  aria-hidden="true"
                  className="inline-block w-[1px] ml-0.5 animate-pulse"
                  style={{
                    backgroundColor: 'currentColor',
                    height: '1em',
                    verticalAlign: 'text-bottom',
                  }}
                />
              )}
            </>
          ) : (
            'Describe your situation, need, or what you’d like to pray about…'
          )}
        </div>
        <div
          className="absolute bottom-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.06)' }}
        >
          <Mic className="w-3.5 h-3.5" style={{ color: '#8891b0' }} aria-hidden="true" />
        </div>
      </div>
      <p className="mt-3 text-[11px] italic text-center font-heading" style={{ color: '#6a7494' }}>
        Your words are the prayer. Scripture is the light.
      </p>
      <div
        className="mt-4 pt-4 flex flex-col sm:flex-row gap-2"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
          style={{
            background: 'rgba(34,41,65,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#dce1ff',
            opacity: text.trim() ? 1 : 0.4,
          }}
        >
          <BookmarkPlus className="w-4 h-4" aria-hidden="true" />
          Save to Journal
        </div>
        <div
          data-cursor-target="shape-button"
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
          style={{
            background: '#e9c349',
            color: '#3c2f00',
            opacity: text.trim() ? 1 : 0.4,
          }}
        >
          <Sparkles className="w-4 h-4" aria-hidden="true" />
          Shape with Scripture
        </div>
      </div>
    </div>
  )
}

// Highlight scripture refs in the streamed prayer so visitors see the
// "Scripture is the light" promise pay off visually as the prayer fills in.
const SHAPE_SCRIPTURE_RE =
  /\b(\d?\s?[A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+(\d{1,3}):(\d{1,3}(?:[–-]\d{1,3})?)/g

function renderShapedProgressive(fullText, len) {
  const parts = []
  const refs = []
  SHAPE_SCRIPTURE_RE.lastIndex = 0
  let m
  while ((m = SHAPE_SCRIPTURE_RE.exec(fullText)) !== null) {
    refs.push({ start: m.index, end: m.index + m[0].length, value: m[0] })
  }
  let pos = 0
  let key = 0
  for (const ref of refs) {
    if (ref.start >= len) break
    if (pos < ref.start) {
      parts.push(
        <Fragment key={`t-${key++}`}>{fullText.slice(pos, Math.min(ref.start, len))}</Fragment>
      )
    }
    if (ref.end <= len) {
      parts.push(
        <span key={`s-${key++}`} style={{ color: '#e9c349', fontWeight: 600 }}>
          {ref.value}
        </span>
      )
      pos = ref.end
    } else {
      parts.push(<Fragment key={`p-${key++}`}>{fullText.slice(ref.start, len)}</Fragment>)
      return parts
    }
  }
  if (pos < len) parts.push(<Fragment key={`t-${key++}`}>{fullText.slice(pos, len)}</Fragment>)
  return parts
}

// What the user "wrote" in the textarea — shown in the WHAT YOU WROTE
// section of the Shape modal. Matches the SHAPED_PRAYER scenario.
const USER_INPUT_DISPLAY =
  'Father, thank you for the strength to get through this week, the family around me, and the way You keep showing up even when I forget to look.'

function ShapingModal({ open, phase, shapedLen, prayThisActive }) {
  // Full Shape-with-Scripture modal — mirrors the real AIShapedPrayerModal.
  // Header (Crown + title + italic subtitle), "WHAT YOU WROTE" recap
  // section, "SHAPED WITH SCRIPTURE" body that streams in, and a three-
  // button action row at the bottom (Pray this / Keep mine + add the
  // verses / Start over).
  const streaming = phase === 'streaming'
  const isDone = phase === 'done'
  const showActions = isDone
  const visibleLen = phase === 'searching' ? 0 : shapedLen

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="absolute inset-0 flex items-center justify-center px-4 py-8"
          style={{ background: 'rgba(11,18,41,0.78)', backdropFilter: 'blur(8px)', zIndex: 30 }}
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 4 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl shadow-2xl flex flex-col"
            style={{
              background: '#141a32',
              border: '1px solid rgba(233,195,73,0.25)',
              // Clamp width so the modal never overflows the demo card on
              // narrow viewports (mobile bento) but still gets a roomy
              // width on desktop.
              width: 'min(576px, calc(100% - 2rem))',
              maxHeight: '92%',
            }}
          >
            {/* Header — Crown icon + title + italic subtitle */}
            <div
              className="flex items-start justify-between gap-3 px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="flex items-start gap-2.5 min-w-0">
                <Crown
                  className="w-4 h-4 mt-0.5 shrink-0"
                  style={{ color: '#e9c349' }}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p
                    className="text-[12px] font-bold uppercase tracking-[0.13em]"
                    style={{ color: '#e9c349' }}
                  >
                    Your Prayer, Shaped by Scripture
                  </p>
                  <p
                    className="font-heading italic text-[12px] mt-0.5"
                    style={{ color: '#a8b0d4' }}
                  >
                    Your words are the prayer. Scripture is the light.
                  </p>
                </div>
              </div>
              <X className="w-4 h-4 shrink-0" style={{ color: '#8891b0' }} aria-hidden="true" />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto dark-scrollbar px-5 py-4">
              {/* WHAT YOU WROTE — always visible after the brief search */}
              <p
                className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5"
                style={{ color: '#8891b0' }}
              >
                What you wrote
              </p>
              <p
                className="font-heading italic text-[14px] leading-relaxed mb-4 pb-4"
                style={{
                  color: '#a8b0d4',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {USER_INPUT_DISPLAY}
              </p>

              {/* SHAPED WITH SCRIPTURE — heading + streaming body */}
              <p
                className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
                style={{ color: '#e9c349' }}
              >
                Shaped with Scripture
              </p>

              {phase === 'searching' ? (
                <div className="flex items-center gap-2 py-2">
                  <p className="font-heading italic text-sm" style={{ color: '#a8b0d4' }}>
                    Searching the Word for verses that meet you in this moment
                  </p>
                  <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                      <motion.span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: '#e9c349' }}
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    const paragraphs = []
                    let offset = 0
                    for (const chunk of SHAPED_PRAYER.split('\n\n')) {
                      paragraphs.push({ chunk, start: offset, end: offset + chunk.length })
                      offset += chunk.length + 2
                    }
                    return paragraphs.map((p, i) => {
                      const localLen = Math.max(0, Math.min(visibleLen - p.start, p.chunk.length))
                      if (localLen <= 0) return null
                      const slice = p.chunk.slice(0, localLen)
                      const isLast = visibleLen > p.start && visibleLen < p.end
                      return (
                        <p
                          key={i}
                          className="font-heading italic text-[14.5px] leading-relaxed"
                          style={{ color: '#dce1ff' }}
                        >
                          {renderShapedProgressive(slice, localLen)}
                          {streaming && isLast && (
                            <span
                              aria-hidden="true"
                              className="inline-block w-[1px] ml-0.5 animate-pulse"
                              style={{
                                backgroundColor: '#e9c349',
                                height: '1em',
                                verticalAlign: 'text-bottom',
                              }}
                            />
                          )}
                        </p>
                      )
                    })
                  })()}
                </div>
              )}
            </div>

            {/* Action row — Pray this / Keep mine + add the verses / Start over.
                Only appears once streaming has resolved. */}
            {showActions && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="px-5 py-3.5 flex flex-col sm:flex-row gap-2 shrink-0"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <motion.div
                  data-cursor-target="pray-this"
                  animate={prayThisActive ? { scale: [1, 0.97, 1] } : { scale: 1 }}
                  transition={{ duration: 0.32 }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
                  style={{
                    background: '#e9c349',
                    color: '#3c2f00',
                    boxShadow: prayThisActive
                      ? '0 0 22px rgba(233,195,73,0.55)'
                      : '0 0 12px rgba(233,195,73,0.2)',
                  }}
                >
                  <Check className="w-4 h-4" aria-hidden="true" />
                  Pray this
                </motion.div>
                <div
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium"
                  style={{
                    background: 'rgba(34,41,65,0.5)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#dce1ff',
                  }}
                >
                  Keep mine + add the verses
                </div>
                <div
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm"
                  style={{ color: '#8891b0' }}
                >
                  <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                  Start over
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function ToggleControl({ view }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      {[
        { id: 'month', label: 'By Month', Icon: BookOpen },
        { id: 'topic', label: 'By Topic', Icon: Layers },
      ].map(({ id, label, Icon }) => {
        const active = view === id
        return (
          <div
            key={id}
            data-cursor-target={`view-${id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
            style={{
              background: active ? '#e9c349' : 'rgba(34,41,65,0.5)',
              color: active ? '#3c2f00' : '#8891b0',
              transition: 'all 250ms ease',
            }}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {label}
          </div>
        )
      })}
    </div>
  )
}

function PrayerCard({ prayer, expanded, editing, editDraft, pendingDelete, showCelebration }) {
  const topicColor = prayer.topicId ? TOPIC_COLORS[prayer.topicId] : null
  const topicLabel = prayer.topicId ? TOPIC_LABEL[prayer.topicId] : null
  const preview = prayer.content.length > 120 ? prayer.content.slice(0, 120) + '…' : prayer.content

  return (
    <motion.div
      data-cursor-target={`prayer-${prayer.id}`}
      layout="position"
      initial={false}
      animate={{
        opacity: pendingDelete ? 0 : 1,
        scale: pendingDelete ? 0.97 : 1,
      }}
      transition={{
        layout: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
        opacity: { duration: 0.32 },
        scale: { duration: 0.32 },
      }}
      className="relative rounded-2xl px-3.5 py-3"
      style={{
        background: prayer.answered ? 'rgba(233,195,73,0.06)' : 'rgba(20,26,50,0.55)',
        border: `1px solid ${prayer.answered ? 'rgba(233,195,73,0.3)' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      {/* Top: preview text + timestamp / source */}
      <div className="flex items-start justify-between gap-3">
        <p
          className="font-heading text-[13px] leading-snug italic flex-1"
          style={{ color: '#dce1ff' }}
        >
          {expanded || editing ? '' : preview}
        </p>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-[11px]" style={{ color: '#6a7494' }}>
            {prayer.createdAt}
          </span>
          <span className="text-[11px]" style={{ color: '#6a7494' }}>
            {prayer.source === 'shaped' ? '✨' : prayer.source === 'generated' ? '🤖' : '✍️'}
          </span>
        </div>
      </div>

      {/* Topic chip + answered badge — flex row so they share a baseline
          and wrap together when both are present. */}
      {(topicLabel || prayer.answered) && !expanded && !editing && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {topicLabel && (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] rounded-full"
              style={{
                color: topicColor,
                border: `1px solid ${topicColor}40`,
                backgroundColor: `${topicColor}18`,
                fontFamily: 'var(--font-body)',
                lineHeight: 1.4,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: topicColor }}
                aria-hidden="true"
              />
              {topicLabel}
            </span>
          )}
          {prayer.answered && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full"
              style={{
                color: '#e9c349',
                background: 'rgba(233,195,73,0.12)',
                border: '1px solid rgba(233,195,73,0.4)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.4,
              }}
            >
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              Answered
            </span>
          )}
        </div>
      )}

      {/* Expanded body */}
      <AnimatePresence initial={false}>
        {(expanded || editing) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              height: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.22 },
            }}
            className="overflow-hidden"
          >
            <div className="mt-3">
              {editing ? (
                <div
                  className="w-full min-h-[120px] rounded-lg p-3 text-sm italic"
                  style={{
                    background: '#0b1229',
                    border: '1px solid #e9c349',
                    color: '#dce1ff',
                    fontFamily: "'Noto Serif', Georgia, serif",
                  }}
                >
                  {editDraft}
                  <span
                    aria-hidden="true"
                    className="inline-block w-[1px] ml-0.5 animate-pulse"
                    style={{
                      backgroundColor: 'currentColor',
                      height: '1em',
                      verticalAlign: 'text-bottom',
                    }}
                  />
                </div>
              ) : (
                <p
                  className="font-heading text-base italic leading-relaxed whitespace-pre-line"
                  style={{ color: '#dce1ff' }}
                >
                  {prayer.content}
                </p>
              )}

              {/* Action row */}
              <div
                className="flex flex-wrap items-center gap-2 mt-3 pt-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
              >
                {editing ? (
                  <>
                    <ActionButton icon={Save} label="Save" tone="gold" target="save-edit" />
                    <ActionButton icon={X} label="Cancel" />
                  </>
                ) : (
                  <>
                    <ActionButton icon={Copy} label="Copy" />
                    <ActionButton icon={Edit3} label="Edit" target="edit-action" />
                    {!prayer.answered && (
                      <ActionButton
                        icon={Sparkles}
                        label="Mark answered"
                        tone="goldGhost"
                        target="mark-answered"
                      />
                    )}
                    <ActionButton
                      icon={Trash2}
                      label="Delete"
                      tone="danger"
                      target="delete-action"
                    />
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Celebration burst when the prayer flips to answered */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest"
              style={{
                background: 'rgba(233,195,73,0.95)',
                color: '#3c2f00',
                boxShadow: '0 8px 28px rgba(233,195,73,0.4)',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
              Answered
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

function ActionButton({ icon: Icon, label, tone, target }) {
  let style
  if (tone === 'gold') {
    style = { background: 'rgba(233,195,73,0.18)', color: '#e9c349' }
  } else if (tone === 'goldGhost') {
    style = { color: '#e9c349' }
  } else if (tone === 'danger') {
    style = { color: '#c97a7a' }
  } else {
    style = { color: '#8891b0' }
  }
  return (
    <div
      data-cursor-target={target}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium"
      style={style}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {label}
    </div>
  )
}

function TopicCardRow({ topicId, prayers }) {
  const color = TOPIC_COLORS[topicId]
  const label = TOPIC_LABEL[topicId]
  const list = prayers.filter(p => p.topicId === topicId)
  const latest = list[0]
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl p-4"
      style={{
        background: 'rgba(20,26,50,0.55)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} aria-hidden="true" />
        <p className="font-heading text-base font-bold" style={{ color: '#dce1ff' }}>
          {label}
        </p>
        <span className="text-[11px]" style={{ color: '#6a7494' }}>
          · {list.length} prayer{list.length === 1 ? '' : 's'}
        </span>
      </div>
      {latest && (
        <p className="font-heading text-xs italic leading-relaxed" style={{ color: '#8891b0' }}>
          &ldquo;{latest.content.slice(0, 110)}…&rdquo;
        </p>
      )}
    </motion.div>
  )
}

// ─── Timeline ─────────────────────────────────────────────────────────

function buildTimeline(setters) {
  const {
    setTab,
    setActiveStarterId,
    setGenerateText,
    setGenerateTyping,
    setShapingOpen,
    setShapePhase,
    setShapedLen,
    setPrayThisActive,
    addPrayer,
    setExpandedId,
    setEditingId,
    setEditDraft,
    markAnswered,
    setCelebrationId,
    setPendingDeleteId,
    removePrayer,
    setView,
    setCursor,
    registerInterval,
    reset,
    finish,
  } = setters

  const events = []
  let t = 0
  const at = (delay, fn) => {
    t += delay
    events.push({ at: t, fn })
  }

  // ── Scene A: Generate — pick starter, type prayer, shape it ────────
  // Cursor anchors on the Gratitude chip via data-cursor-target, then
  // moves to the Shape button after the user extension finishes typing.
  // No more pixel/percentage guessing — refs do the targeting.
  at(700, () => setCursor({ target: 'starter-gratitude', click: false }))
  at(900, () => {
    setActiveStarterId('gratitude')
    setCursor({ target: 'starter-gratitude', click: true })
  })
  at(450, () => {
    setActiveStarterId(null)
    setGenerateText('Father, thank you for ')
    setGenerateTyping(true)
    // Hide the cursor while the user extension types — comes back when
    // the timeline picks the next target.
    setCursor(null)
  })
  // Type the user extension char-by-char.
  at(400, () => {
    const base = 'Father, thank you for '
    let i = 0
    const id = setInterval(() => {
      i += 1
      setGenerateText(base + GRATITUDE_EXTENSION.slice(0, i))
      if (i >= GRATITUDE_EXTENSION.length) {
        clearInterval(id)
        setGenerateTyping(false)
      }
    }, 28)
    registerInterval(id)
  })
  // After typing finishes, cursor heads to the Shape CTA and "clicks".
  at(GRATITUDE_EXTENSION.length * 28 + 700, () =>
    setCursor({ target: 'shape-button', click: false })
  )
  at(700, () => {
    setCursor({ target: 'shape-button', click: true })
    setShapingOpen(true)
    setShapePhase('searching')
    setShapedLen(0)
  })
  // Brief search animation, then stream the AI prayer.
  at(900, () => setShapePhase('streaming'))
  at(300, () => {
    let i = 0
    const id = setInterval(() => {
      i += 4
      setShapedLen(Math.min(i, SHAPED_PRAYER.length))
      if (i >= SHAPED_PRAYER.length) {
        clearInterval(id)
        setShapePhase('done')
      }
    }, 28)
    registerInterval(id)
  })
  // Streaming complete + a beat to absorb the prayer, then the cursor
  // glides to "Pray this" (leftmost button in the action row), pulses
  // it, and the modal commits the prayer to the journal.
  // After streaming + a beat, cursor glides to "Pray this" and clicks.
  at(Math.ceil(SHAPED_PRAYER.length / 4) * 28 + 1700, () =>
    setCursor({ target: 'pray-this', click: false })
  )
  at(550, () => {
    setCursor({ target: 'pray-this', click: true })
    setPrayThisActive(true)
  })
  at(600, () => {
    setShapingOpen(false)
    setPrayThisActive(false)
    addPrayer({
      id: 999,
      content: SHAPED_PRAYER,
      topicId: 'family',
      createdAt: 'Just now',
      source: 'shaped',
      answered: false,
    })
    setTab('journal')
    setCursor(null)
  })

  // ── Scene B: Journal — open prayer 1, Mark Answered ────────────────
  at(1100, () => setCursor({ target: 'prayer-1', click: false }))
  at(500, () => setCursor({ target: 'prayer-1', click: true }))
  at(200, () => setExpandedId(1))
  at(2400, () => setCursor({ target: 'mark-answered', click: false }))
  at(500, () => {
    setCursor({ target: 'mark-answered', click: true })
    setCelebrationId(1)
    markAnswered(1)
  })
  at(1500, () => setCelebrationId(null))
  at(700, () => setExpandedId(null))

  // ── Scene C: Edit prayer 2 ─────────────────────────────────────────
  at(900, () => setCursor({ target: 'prayer-2', click: false }))
  at(450, () => setCursor({ target: 'prayer-2', click: true }))
  at(200, () => setExpandedId(EDIT_TARGET_ID))
  at(1000, () => setCursor({ target: 'edit-action', click: false }))
  at(450, () => {
    setCursor({ target: 'edit-action', click: true })
    const base = INITIAL_PRAYERS.find(p => p.id === EDIT_TARGET_ID).content
    setEditingId(EDIT_TARGET_ID)
    setEditDraft(base)
  })
  at(400, () => {
    const base = INITIAL_PRAYERS.find(p => p.id === EDIT_TARGET_ID).content
    let i = 0
    const id = setInterval(() => {
      i += 1
      setEditDraft(base + EDIT_ADDITION.slice(0, i))
      if (i >= EDIT_ADDITION.length) clearInterval(id)
    }, 30)
    registerInterval(id)
  })
  at(EDIT_ADDITION.length * 30 + 600, () => setCursor({ target: 'save-edit', click: false }))
  at(450, () => {
    setCursor({ target: 'save-edit', click: true })
    setEditingId(null)
  })
  at(1300, () => setExpandedId(null))

  // ── Scene D: Delete prayer 3 ───────────────────────────────────────
  at(900, () => setCursor({ target: 'prayer-3', click: false }))
  at(450, () => setCursor({ target: 'prayer-3', click: true }))
  at(200, () => setExpandedId(3))
  at(1200, () => setCursor({ target: 'delete-action', click: false }))
  at(450, () => {
    setCursor({ target: 'delete-action', click: true })
    setPendingDeleteId(3)
  })
  at(550, () => {
    removePrayer(3)
    setPendingDeleteId(null)
    setExpandedId(null)
  })

  // ── Scene E: Toggle to By Topic ────────────────────────────────────
  at(900, () => setCursor({ target: 'view-topic', click: false }))
  at(450, () => {
    setCursor({ target: 'view-topic', click: true })
    setView('topic')
  })
  at(3400, () => setView('month'))

  // ── Hold + reset + loop ────────────────────────────────────────────
  at(1500, () => {
    reset()
    setCursor(null)
  })
  at(400, () => finish())

  return events
}

// ─── Main component ───────────────────────────────────────────────────

export default function PrayerDemo() {
  const containerRef = useRef(null)
  const [cycleId, setCycleId] = useState(0)
  const [tab, setTab] = useState('generate')
  const [activeStarterId, setActiveStarterId] = useState(null)
  const [generateText, setGenerateText] = useState('')
  const [generateTyping, setGenerateTyping] = useState(false)
  const [shapingOpen, setShapingOpen] = useState(false)
  const [shapePhase, setShapePhase] = useState('searching')
  const [shapedLen, setShapedLen] = useState(0)
  const [prayThisActive, setPrayThisActive] = useState(false)
  const [cursor, setCursor] = useState(null)
  const [prayers, setPrayers] = useState(INITIAL_PRAYERS)
  const [expandedId, setExpandedId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState('')
  const [celebrationId, setCelebrationId] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [view, setView] = useState('month')

  const intervalsRef = useRef([])

  useEffect(() => {
    intervalsRef.current = []
    const setters = {
      setTab,
      setActiveStarterId,
      setGenerateText,
      setGenerateTyping,
      setShapingOpen,
      setShapePhase,
      setShapedLen,
      setPrayThisActive,
      setCursor,
      addPrayer: p => setPrayers(prev => (prev.find(x => x.id === p.id) ? prev : [p, ...prev])),
      setExpandedId,
      setEditingId,
      setEditDraft,
      markAnswered: id =>
        setPrayers(prev => prev.map(p => (p.id === id ? { ...p, answered: true } : p))),
      setCelebrationId,
      setPendingDeleteId,
      removePrayer: id => setPrayers(prev => prev.filter(p => p.id !== id)),
      setView,
      registerInterval: id => intervalsRef.current.push(id),
      reset: () => {
        setTab('generate')
        setActiveStarterId(null)
        setGenerateText('')
        setGenerateTyping(false)
        setShapingOpen(false)
        setShapePhase('searching')
        setShapedLen(0)
        setPrayThisActive(false)
        setCursor(null)
        setPrayers(INITIAL_PRAYERS)
        setExpandedId(null)
        setEditingId(null)
        setEditDraft('')
        setCelebrationId(null)
        setPendingDeleteId(null)
        setView('month')
      },
      finish: () => setCycleId(c => c + 1),
    }

    const events = buildTimeline(setters)
    const timers = events.map(e => setTimeout(e.fn, e.at))

    return () => {
      timers.forEach(clearTimeout)
      intervalsRef.current.forEach(clearInterval)
      intervalsRef.current = []
    }
  }, [cycleId])

  // Group prayers by month label for the By Month view. With the demo
  // dataset all prayers fall under April 2026 (plus any "Just now" entries
  // tagged to the same group for simplicity).
  const monthList = prayers.slice().sort((a, b) => {
    if (a.createdAt === 'Just now') return -1
    if (b.createdAt === 'Just now') return 1
    return 0
  })

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl"
      style={{
        backgroundColor: '#0b1229',
        border: '1px solid rgba(233,195,73,0.15)',
        // Sized to fill its bento expansion area (2 cols × 3 rows on
        // the right of the bento grid). Rows in the bento are pinned
        // at 320px each by the displaced-card height fix, so 3 rows =
        // 960px. Demo height 920 leaves room for the bento card's
        // bottom CTA strip without forcing extra row growth.
        height: 920,
      }}
    >
      {/* Soft gold radial wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(233,195,73,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative px-6 md:px-10 py-8 md:py-10 max-w-3xl mx-auto">
        <PrayerHeader tab={tab} />

        <div className="flex justify-center mb-6" style={{ color: 'rgba(233,195,73,0.7)' }}>
          <OrnamentDivider className="w-32 h-2" />
        </div>

        <Tabs tab={tab} />

        <AnimatePresence mode="wait">
          {tab === 'generate' && (
            <motion.div
              key="generate"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <StarterChips activeStarterId={activeStarterId} />
              <TextareaCard text={generateText} isTyping={generateTyping} />
            </motion.div>
          )}

          {tab === 'journal' && (
            <motion.div
              key="journal"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
            >
              <ToggleControl view={view} />

              <AnimatePresence mode="wait">
                {view === 'month' ? (
                  <motion.div
                    key="month-view"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-baseline gap-2 mb-3 px-1">
                      <span className="text-sm font-semibold" style={{ color: '#dce1ff' }}>
                        April 2026
                      </span>
                      <span className="text-[11px]" style={{ color: '#6a7494' }}>
                        · {monthList.length} prayer{monthList.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    {/* Internal scroll so the prayer list fits within
                        the demo card height (620px) even when one card
                        is expanded. Outer demo container is overflow:
                        hidden so the list scrolls inside instead of
                        forcing the bento grid row to grow. */}
                    <div
                      className="space-y-2 overflow-y-auto pr-1 dark-scrollbar"
                      style={{ maxHeight: 580 }}
                    >
                      <AnimatePresence initial={false}>
                        {monthList.map(p => (
                          <PrayerCard
                            key={p.id}
                            prayer={p}
                            expanded={expandedId === p.id}
                            editing={editingId === p.id}
                            editDraft={editDraft}
                            pendingDelete={pendingDeleteId === p.id}
                            showCelebration={celebrationId === p.id}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="topic-view"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="space-y-3">
                      {['family', 'college', 'daily'].map(tid => (
                        <TopicCardRow key={tid} topicId={tid} prayers={prayers} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ShapingModal
        open={shapingOpen}
        phase={shapePhase}
        shapedLen={shapedLen}
        prayThisActive={prayThisActive}
      />
      <DemoCursor cursor={cursor} containerRef={containerRef} />
    </div>
  )
}
